import React, { useState, useEffect } from 'react'
import { ArrowLeft, Eye, EyeOff } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { API_BASE_URL } from '../config/api'
import toast from 'react-hot-toast'
import { useAuth } from '../hooks/useAuth'

const PDFVisualConfig = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [loadingConfig, setLoadingConfig] = useState(true)
  const [config, setConfig] = useState({
    show_signature_area_border: true,
    show_pdf_info_label: true,
    show_calibration_grid: false,
    show_calibration_markers: false,
    print_calibration_grid: false,
    print_calibration_markers: false
  })
  
  // Verificar permisos: solo administrador y supervisor pueden modificar
  const isAdmin = user?.role === 'administrador' || user?.is_admin
  const isSupervisor = user?.role === 'supervisor'
  const canEdit = isAdmin || isSupervisor

  useEffect(() => {
    loadConfig()
  }, [])

  const loadConfig = async () => {
    try {
      setLoadingConfig(true)
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/config/system`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setConfig({
          show_signature_area_border: data.show_signature_area_border ?? true,
          show_pdf_info_label: data.show_pdf_info_label ?? true,
          show_calibration_grid: data.show_calibration_grid ?? false,
          show_calibration_markers: data.show_calibration_markers ?? false,
          print_calibration_grid: data.print_calibration_grid ?? false,
          print_calibration_markers: data.print_calibration_markers ?? false
        })
      } else {
        toast.error('Error al cargar la configuración')
      }
    } catch (error) {
      console.error('Error al cargar configuración:', error)
      toast.error('Error al cargar la configuración')
    } finally {
      setLoadingConfig(false)
    }
  }

  const handleSave = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/config/system`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(config)
      })

      if (response.ok) {
        toast.success('Configuración guardada exitosamente')
      } else {
        const error = await response.json()
        toast.error(error.detail || 'Error al guardar la configuración')
      }
    } catch (error) {
      console.error('Error al guardar configuración:', error)
      toast.error('Error al guardar la configuración')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen" style={{background: 'var(--background-color)'}}>
      {/* Header */}
      <header className="main-header">
        <div className="header-content">
          <div className="header-left">
            <button
              onClick={() => navigate('/config')}
              className="mr-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div className="header-title">
              <h1>Configuración de Visualización PDF</h1>
              <p>Gestiona la visualización de elementos en el editor de PDF</p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loadingConfig ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <div className="card">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Opciones de Visualización
              </h2>
              <p className="text-gray-600 text-sm">
                Controla qué elementos visuales se muestran en el editor de PDF para efectos de monitoreo y depuración.
              </p>
            </div>

            <div className="space-y-6">
              {/* Opción 1: Mostrar borde del área de firma */}
              <div className="border border-gray-200 rounded-lg p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      {config.show_signature_area_border ? (
                        <Eye className="h-5 w-5 text-green-600" />
                      ) : (
                        <EyeOff className="h-5 w-5 text-gray-400" />
                      )}
                      <h3 className="text-lg font-semibold text-gray-900">
                        Área Disponible para Firma
                      </h3>
                    </div>
                    <p className="text-gray-600 text-sm mb-4">
                      Muestra el borde rojo punteado y la etiqueta con las dimensiones del área disponible para colocar firmas en el PDF.
                    </p>
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center space-x-2 text-sm text-gray-700">
                        <div className="w-4 h-4 border-2 border-red-500 border-dashed rounded"></div>
                        <span>Borde rojo punteado alrededor del área de firma</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-700 mt-2">
                        <div className="w-20 h-6 bg-red-600 rounded-full"></div>
                        <span>Etiqueta roja con dimensiones (ej: 892px × 1262px)</span>
                      </div>
                    </div>
                  </div>
                  <label className={`relative inline-flex items-center ml-4 ${canEdit ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                    <input
                      type="checkbox"
                      checked={config.show_signature_area_border}
                      onChange={(e) => canEdit && setConfig({ ...config, show_signature_area_border: e.target.checked })}
                      disabled={!canEdit}
                      className="sr-only peer"
                    />
                    <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 ${!canEdit ? 'opacity-50' : ''}`}></div>
                  </label>
                </div>
              </div>

              {/* Opción 2: Mostrar información del PDF */}
              <div className="border border-gray-200 rounded-lg p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      {config.show_pdf_info_label ? (
                        <Eye className="h-5 w-5 text-green-600" />
                      ) : (
                        <EyeOff className="h-5 w-5 text-gray-400" />
                      )}
                      <h3 className="text-lg font-semibold text-gray-900">
                        Información del PDF
                      </h3>
                    </div>
                    <p className="text-gray-600 text-sm mb-4">
                      Muestra el borde verde punteado alrededor del PDF y la etiqueta con las dimensiones y escala del documento.
                    </p>
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center space-x-2 text-sm text-gray-700">
                        <div className="w-4 h-4 border-2 border-green-500 border-dashed rounded"></div>
                        <span>Borde verde punteado alrededor del PDF</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-700 mt-2">
                        <div className="w-32 h-6 bg-green-600 rounded-full"></div>
                        <span>Etiqueta verde con dimensiones y escala (ej: PDF: 893 × 1263 pt · Escala 100%)</span>
                      </div>
                    </div>
                  </div>
                  <label className={`relative inline-flex items-center ml-4 ${canEdit ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                    <input
                      type="checkbox"
                      checked={config.show_pdf_info_label}
                      onChange={(e) => canEdit && setConfig({ ...config, show_pdf_info_label: e.target.checked })}
                      disabled={!canEdit}
                      className="sr-only peer"
                    />
                    <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 ${!canEdit ? 'opacity-50' : ''}`}></div>
                  </label>
                </div>
              </div>

              {/* Opción 3: Mostrar cuadrícula de calibración */}
              <div className="border border-gray-200 rounded-lg p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      {config.show_calibration_grid ? (
                        <Eye className="h-5 w-5 text-green-600" />
                      ) : (
                        <EyeOff className="h-5 w-5 text-gray-400" />
                      )}
                      <h3 className="text-lg font-semibold text-gray-900">
                        Cuadrícula de Calibración
                      </h3>
                    </div>
                    <p className="text-gray-600 text-sm mb-4">
                      Muestra una cuadrícula de referencia con líneas cada 50 puntos para facilitar la calibración y posicionamiento preciso de firmas.
                    </p>
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center space-x-2 text-sm text-gray-700">
                        <svg width="40" height="40" viewBox="0 0 40 40" className="border border-gray-300">
                          <line x1="0" y1="0" x2="40" y2="0" stroke="#d1d5db" strokeWidth="0.5" />
                          <line x1="0" y1="20" x2="40" y2="20" stroke="#d1d5db" strokeWidth="0.5" />
                          <line x1="0" y1="40" x2="40" y2="40" stroke="#d1d5db" strokeWidth="0.5" />
                          <line x1="0" y1="0" x2="0" y2="40" stroke="#d1d5db" strokeWidth="0.5" />
                          <line x1="20" y1="0" x2="20" y2="40" stroke="#d1d5db" strokeWidth="0.5" />
                          <line x1="40" y1="0" x2="40" y2="40" stroke="#d1d5db" strokeWidth="0.5" />
                        </svg>
                        <span>Cuadrícula gris con líneas cada 50 puntos</span>
                      </div>
                    </div>
                  </div>
                  <label className={`relative inline-flex items-center ml-4 ${canEdit ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                    <input
                      type="checkbox"
                      checked={config.show_calibration_grid}
                      onChange={(e) => canEdit && setConfig({ ...config, show_calibration_grid: e.target.checked })}
                      disabled={!canEdit}
                      className="sr-only peer"
                    />
                    <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 ${!canEdit ? 'opacity-50' : ''}`}></div>
                  </label>
                </div>
              </div>

              {/* Opción 4: Mostrar marcadores de calibración */}
              <div className="border border-gray-200 rounded-lg p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      {config.show_calibration_markers ? (
                        <Eye className="h-5 w-5 text-green-600" />
                      ) : (
                        <EyeOff className="h-5 w-5 text-gray-400" />
                      )}
                      <h3 className="text-lg font-semibold text-gray-900">
                        Marcadores de Calibración
                      </h3>
                    </div>
                    <p className="text-gray-600 text-sm mb-4">
                      Muestra marcadores X rojos en 4 puntos de referencia (X=100, Y=100; X=400, Y=100; X=100, Y=600; X=400, Y=600) para calibración precisa de la posición de firmas.
                    </p>
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center space-x-2 text-sm text-gray-700">
                        <svg width="40" height="40" viewBox="0 0 40 40">
                          <line x1="5" y1="5" x2="35" y2="35" stroke="#ef4444" strokeWidth="3" />
                          <line x1="5" y1="35" x2="35" y2="5" stroke="#ef4444" strokeWidth="3" />
                          <circle cx="20" cy="20" r="22" fill="none" stroke="#ef4444" strokeWidth="2" />
                        </svg>
                        <span>Marcadores X rojos con etiquetas en puntos de referencia</span>
                      </div>
                    </div>
                  </div>
                  <label className={`relative inline-flex items-center ml-4 ${canEdit ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                    <input
                      type="checkbox"
                      checked={config.show_calibration_markers}
                      onChange={(e) => canEdit && setConfig({ ...config, show_calibration_markers: e.target.checked })}
                      disabled={!canEdit}
                      className="sr-only peer"
                    />
                    <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 ${!canEdit ? 'opacity-50' : ''}`}></div>
                  </label>
                </div>
              </div>

              {/* Opción 5: Imprimir cuadrícula en PDF final */}
              <div className="border border-gray-200 rounded-lg p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      {config.print_calibration_grid ? (
                        <Eye className="h-5 w-5 text-green-600" />
                      ) : (
                        <EyeOff className="h-5 w-5 text-gray-400" />
                      )}
                      <h3 className="text-lg font-semibold text-gray-900">
                        Imprimir Cuadrícula en PDF Final
                      </h3>
                    </div>
                    <p className="text-gray-600 text-sm mb-4">
                      Si está habilitado, la cuadrícula de calibración se imprimirá en el documento PDF final firmado.
                    </p>
                  </div>
                  <label className={`relative inline-flex items-center ml-4 ${canEdit ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                    <input
                      type="checkbox"
                      checked={config.print_calibration_grid}
                      onChange={(e) => canEdit && setConfig({ ...config, print_calibration_grid: e.target.checked })}
                      disabled={!canEdit}
                      className="sr-only peer"
                    />
                    <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 ${!canEdit ? 'opacity-50' : ''}`}></div>
                  </label>
                </div>
              </div>

              {/* Opción 6: Imprimir marcadores en PDF final */}
              <div className="border border-gray-200 rounded-lg p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      {config.print_calibration_markers ? (
                        <Eye className="h-5 w-5 text-green-600" />
                      ) : (
                        <EyeOff className="h-5 w-5 text-gray-400" />
                      )}
                      <h3 className="text-lg font-semibold text-gray-900">
                        Imprimir Marcadores en PDF Final
                      </h3>
                    </div>
                    <p className="text-gray-600 text-sm mb-4">
                      Si está habilitado, los marcadores X rojos de calibración se imprimirán en el documento PDF final firmado.
                    </p>
                  </div>
                  <label className={`relative inline-flex items-center ml-4 ${canEdit ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                    <input
                      type="checkbox"
                      checked={config.print_calibration_markers}
                      onChange={(e) => canEdit && setConfig({ ...config, print_calibration_markers: e.target.checked })}
                      disabled={!canEdit}
                      className="sr-only peer"
                    />
                    <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 ${!canEdit ? 'opacity-50' : ''}`}></div>
                  </label>
                </div>
              </div>
            </div>

            {/* Botón de guardar */}
            <div className="mt-8 flex justify-end">
              <button
                onClick={handleSave}
                disabled={loading || !canEdit}
                className="btn-primary px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Guardando...' : 'Guardar Configuración'}
              </button>
            </div>
            
            {/* Mensaje si no tiene permisos */}
            {!canEdit && (
              <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>Nota:</strong> Solo los administradores y supervisores pueden modificar esta configuración.
                </p>
              </div>
            )}

            {/* Información adicional */}
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Nota:</strong> Estos ajustes afectan la visualización en el editor de PDF para todos los usuarios. 
                Útil para monitoreo y depuración. Los cambios se aplicarán inmediatamente después de guardar.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default PDFVisualConfig

